import { useState } from 'react';
import { Box, useBreakpoints } from '@monorepo/nova-ui';
import { useNotifications } from '../../../hooks';
import {
  NotificationsCleared,
  NotificationsContent,
  NotificationsEmpty,
  NotificationsError,
  NotificationsLoading,
} from '../notifications-states';
import { NotificationsFooter } from './notifications-footer';
import { NotificationsHeader } from './notifications-header';

type Props = {
  setOpen: (open: boolean) => void;
};

export const NotificationsLayout = ({ setOpen }: Props) => {
  const { isMobile } = useBreakpoints();
  const { current, history, unreadCount, isLoading, error } =
    useNotifications();
  const [showHistory, setShowHistory] = useState<boolean>(false);

  const displayedNotifications = showHistory
    ? [...current, ...history]
    : current;

  const isEmpty = history.length === 0 && current.length === 0;
  const isCleared = current.length === 0 && !showHistory;

  const state = (() => {
    if (isLoading) {
      return {
        Component: NotificationsLoading,
        readAllEnabled: false,
        historyEnabled: false,
      };
    }

    if (error) {
      return {
        Component: NotificationsError,
        readAllEnabled: false,
        historyEnabled: false,
      };
    }

    if (isEmpty) {
      return {
        Component: NotificationsEmpty,
        readAllEnabled: false,
        historyEnabled: false,
      };
    }

    if (isCleared) {
      return {
        Component: NotificationsCleared,
        readAllEnabled: false,
        historyEnabled: history.length > 0,
      };
    }

    return {
      Component: NotificationsContent,
      readAllEnabled: unreadCount > 0,
      historyEnabled: showHistory ? false : history.length > 0,
    };
  })();

  return (
    <Box
      display="flex"
      flexDirection="column"
      {...(isMobile && { flex: 1 })}
      {...(!isMobile && { maxHeight: '75vh' })}
    >
      <NotificationsHeader
        readAllEnabled={state.readAllEnabled}
        setOpen={setOpen}
      />
      <Box
        {...(isMobile && { flex: 1 })}
        paddingX="16px"
        paddingTop={isMobile ? '16px' : 0}
        paddingBottom={isMobile ? '16px' : state.historyEnabled ? 0 : '16px'}
        sx={{ backgroundColor: 'background.contrast' }}
      >
        <state.Component displayedNotifications={displayedNotifications} />
      </Box>
      <NotificationsFooter
        readAllEnabled={state.readAllEnabled}
        historyEnabled={state.historyEnabled}
        setShowHistory={setShowHistory}
      />
    </Box>
  );
};
